import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutsideClick } from '@chakra-ui/react';
import { animated, useSpring } from '@react-spring/web';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { ArrowDownIcon, CalendarIcon } from '@/components/common/Icon';
import { useModal } from '@/components/hooks/useModal';
import { useFiltersContext } from '@/components/pages/SituationalAwareness/context/FiltersContextProvider';
import { DateRange } from '@/types';
import { TimeControlRangePicker } from '../../../../common/TimeControlRangePicker';
import { TIME_CONTROL_BUTTON_SELECTOR, TimeFilterPresets } from './TimeFilterControl.const';
import styles from './TimeFilterControl.module.scss';
import { TimeControlButton } from './components/TimeControlButton';
import { TimeControlPanel } from './components/TimeControlPanel';
import { useButtonLabelByPreset } from './hooks/useButtonLabelByPreset';
import { useTimePresetFromDateRange } from './hooks/useTimePresetFromDateRange';
import { timePresetToDateRange } from './utils/timePresetToDateRange';

interface TimeFilterControlProps {
  withLeftOffset?: boolean;
}

export const TimeFilterControl: React.FC<TimeFilterControlProps> = ({ withLeftOffset }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [activePreset, setActivePreset] = useState<TimeFilterPresets | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    applyFilters,
    filters: { dateRange },
  } = useFiltersContext();
  const getButtonLabel = useButtonLabelByPreset();
  const getPresetFromDateRange = useTimePresetFromDateRange();
  const arrowIconAnimationStyles = useSpring({
    transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
  });
  const panelAnimationStyles = useSpring({
    height: isOpen ? 80 : 0,
    config: { tension: 80, friction: 15 },
  });
  const applyDateFilter = useCallback((dateRange: DateRange | null) => {
    if (dateRange) {
      applyFilters({
        dateRange: [dateRange],
      });

      setIsOpen(false);
    }
  }, []);

  const { show: showDatePicker, modal } = useModal((onClose) => (
    <TimeControlRangePicker onClose={onClose} applyFilters={applyDateFilter} selectedDateRange={dateRange} />
  ));

  const handleButtonClick = useCallback(() => setIsOpen((prevState) => !prevState), [isOpen]);

  const handlePresetChange = useCallback((preset: TimeFilterPresets) => {
    if (preset === TimeFilterPresets.CUSTOM_RANGE) {
      showDatePicker();
    } else {
      setActivePreset(preset);
      applyDateFilter(timePresetToDateRange(preset));
    }
  }, []);

  useEffect(() => {
    if (dateRange && !isEmpty(dateRange)) {
      const [{ from, to }] = dateRange;
      const preset = getPresetFromDateRange({ from, to });

      if (preset) {
        setActivePreset(preset);
      }
    }
  }, [dateRange]);

  useOutsideClick({
    ref: panelRef,
    handler: (event) => {
      // Prevent closing the panel when clicking on the button itself, handleButtonClick handler will be used for this
      if ((event.target as HTMLElement).closest(`[data-type="${TIME_CONTROL_BUTTON_SELECTOR}"]`)) return;

      if (isOpen) {
        setIsOpen(false);
      }
    },
  });

  return (
    <div
      className={classnames('absolute bottom-0 right-0 w-full', styles.root, { [styles.leftOffset]: !!withLeftOffset })}
    >
      <div className="flex justify-center">
        <TimeControlButton
          data-type={TIME_CONTROL_BUTTON_SELECTOR}
          label={getButtonLabel(activePreset)}
          startIcon={<CalendarIcon />}
          endIcon={
            <animated.div style={arrowIconAnimationStyles}>
              <ArrowDownIcon />
            </animated.div>
          }
          onClick={handleButtonClick}
        />
      </div>
      <animated.div style={panelAnimationStyles} className="overflow-hidden">
        <TimeControlPanel activePreset={activePreset} onPresetChange={handlePresetChange} ref={panelRef} />
      </animated.div>
      {modal}
    </div>
  );
};

export default TimeFilterControl;
